/** A drag only becomes a selection on entering a second cell, so a press and release is still a click. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { copyToClipboard } from '@app/utils/helpers/copy';

/** A sprint cell carries two figures and a spreadsheet cell holds one, so the choice is the reader's. */
export type CopyUnit = 'lead' | 'hours' | 'cost';

/** Held here, not on the clipboard: a custom clipboard type is not carried reliably. */
let lastCostCopy: string | null = null;

/** Put on a field that holds euros, so a rate copied from the cost matrix may be pasted into it. */
export const moneyFieldSelector = '[data-money-field]';
export const moneyField = { 'data-money-field': '' } as const;

/** How far into the corner cell a press still takes the handle, in pixels. */
const GRIP_REACH = 8;

/** Where a cell is: the row it is on, and the column it is under. */
export interface CellAddress {
  row: string;
  col: string;
}

/** A block of cells, held as the cell it was anchored at and the one it currently reaches to. */
interface CellRect {
  anchor: CellAddress;
  focus: CellAddress;
}

/** Which sides of the block a cell is on, so the grid can draw one border around the whole of it. */
export interface CellEdges {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
  /** The block's bottom right corner, where the fill handle sits. */
  grip?: boolean;
  /** Part of where a fill is being dragged to, drawn dashed until it is let go. */
  pending?: boolean;
}

/** Indices into the grid's rows and columns, both ends inclusive. */
export interface Span {
  rows: [number, number];
  cols: [number, number];
}

/** Where dragging the fill handle reaches: along whichever axis the pointer has gone further outside the block. */
export const fillReach = (block: Span, row: number, col: number): Span | null => {
  const [top, bottom] = block.rows;
  const [left, right] = block.cols;
  /** How far past an edge an index is, negative before the first: nothing while it is between them. */
  const past = (index: number, first: number, last: number): number => {
    if (index > last) return index - last;
    if (index < first) return index - first;
    return 0;
  };
  const down = past(row, top, bottom);
  const across = past(col, left, right);
  if (!down && !across) return null;
  if (Math.abs(down) >= Math.abs(across)) {
    return { rows: down > 0 ? [bottom + 1, row] : [row, top - 1], cols: block.cols };
  }
  return { rows: block.rows, cols: across > 0 ? [right + 1, col] : [col, left - 1] };
};

/** The index a filled cell copies: the block's figure-holding indices repeated onwards, or backwards from its end. */
export const fillSource = (sources: number[], [first, last]: [number, number], index: number): number | null => {
  if (index >= first && index <= last) return index;
  if (!sources.length) return null;
  if (index > last) return sources[(index - last - 1) % sources.length];
  return sources[sources.length - 1 - ((first - 1 - index) % sources.length)];
};

/** A block of cells as a spreadsheet reads it: a tab between columns, a newline between rows. */
export const toTsv = (rows: string[][]): string => rows.map(row => row.join('\t')).join('\n');

/** Excel ends the block with a line break too, so the trailing one is not an empty row. */
export const fromTsv = (text: string): string[][] =>
  text
    .replace(/\r\n?/g, '\n')
    .replace(/\n$/, '')
    .split('\n')
    .map(line => line.split('\t'));

/** The two indices a pair of keys spans, smallest first, or nothing where either has gone. */
const spanOf = (keys: string[], from: string, to: string): [number, number] | null => {
  const first = keys.indexOf(from);
  const last = keys.indexOf(to);
  if (first === -1 || last === -1) return null;
  return first <= last ? [first, last] : [last, first];
};

export interface CellRange {
  /** Whether the cell is in the block, for the fill the grid draws over it. */
  selected: (row: string, col: string) => boolean;
  /** Copy the block as one figure or the other, which is what the menu on it offers. */
  copyAs: (unit: CopyUnit) => void;
  /** Empty the block's editable cells, which is what Delete does and what the menu offers. */
  clearCells: () => void;
  /** Whether the block holds anything that can be emptied, so the menu only offers it where it can. */
  hasEditable: () => boolean;
  /** Whether there is a block at all, so the card can say what copying it would take. */
  hasBlock: () => boolean;
  /** Where a menu was asked for, and the way to shut it. */
  menuAt: { x: number; y: number } | null;
  /** Whether it was the copy key that opened it, which asks about copying and nothing else. */
  menuIsCopy: boolean;
  closeMenu: () => void;
  /** Set where money was pasted into cells that hold hours, so the grid can say why nothing happened. */
  costPasteRefused: boolean;
  /** Set where the browser refused the clipboard, so a menu that did nothing says so. */
  copyFailed: boolean;
  /** Which sides of the block the cell is on. All false where it is not in one. */
  edges: (row: string, col: string) => CellEdges;
  /** Spread onto every cell. The grid keeps its own click and focus handlers. */
  cellHandlers: (
    row: string,
    col: string,
  ) => {
    onPointerDown: (event: React.PointerEvent) => void;
    onPointerEnter: (event: React.PointerEvent) => void;
    onContextMenu: (event: React.MouseEvent) => void;
  };
  /** Spread onto the cell being typed into, which is still the block and still has its fill handle. */
  openCellHandlers: (
    row: string,
    col: string,
  ) => {
    onPointerDown: (event: React.PointerEvent) => void;
  };
  /** Called before the grid opens a cell: the end of a drag is not somebody asking to type. */
  claimedClick: () => boolean;
  /** Only while no cell is open: a cell being typed into owns the clipboard and the arrow keys. */
  containerHandlers: {
    ref: React.RefObject<HTMLDivElement>;
    tabIndex: -1;
    onCopy: (event: React.ClipboardEvent) => void;
    onPaste: (event: React.ClipboardEvent) => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
  };
  /** Called as a cell opens, so a pasted block lands on the cell somebody just clicked. */
  anchorAt: (row: string, col: string) => void;
  /** Drop the block, for a grid that has just been saved or paged away from. */
  clear: () => void;
}

export const useCellRange = ({
  rows,
  cols,
  readCell,
  writeCells,
  isEditable,
  passiveRows,
  resetOn,
  allowClear = true,
  leadIsCost = false,
  twoFigures = false,
  readForFill = (row, col) => readCell(row, col, 'lead'),
  fillsFrom = () => true,
  onCopyResult,
  onCostPasteResult,
}: {
  /** The rows on screen, top to bottom. A row that is not here cannot be selected. */
  rows: string[];
  /** The columns on screen, left to right. */
  cols: string[];
  /** What the cell puts on the clipboard, for whichever figure the copy asked for. */
  readCell: (row: string, col: string, unit: CopyUnit) => string;
  /** What paste and delete write. Absent on a grid nobody types into, which can still be copied. */
  writeCells?: (cells: { row: string; col: string; value: string }[]) => void;
  /** Which cells paste and delete may touch. Everything, where the grid does not say. */
  isEditable?: (row: string, col: string) => boolean;
  /** In `rows` so the block keeps one shape, out of what it reads. First passed by slice 6's grid. */
  passiveRows?: string[];
  /** A block belongs to these cells, so it goes when they do. */
  resetOn?: string;
  /** Whether the cells lead with money, which is what a copy of the leading figure is. */
  leadIsCost?: boolean;
  /** Whether emptying a cell is a value at all. False where blank cannot be saved, so Delete does nothing. */
  allowClear?: boolean;
  /** Told the outcome of every copy, for a card whose two grids share one line to say it on. */
  onCopyResult?: (failed: boolean) => void;
  /** Told of every refused cost paste, for the same reason. */
  onCostPasteResult?: (refused: boolean) => void;
  /** Where a cell carries two, the copy key asks which is wanted. No grid here has two until slice 6. */
  twoFigures?: boolean;
  /** What dragging the fill handle repeats: what the cell is typed in. Its leading figure, where not said. */
  readForFill?: (row: string, col: string) => string;
  /** Whether one cell may fill another, for a grid whose rows hold different kinds of figure. */
  fillsFrom?: (from: CellAddress, to: CellAddress) => boolean;
}): CellRange => {
  const [rect, setRect] = useState<CellRect | null>(null);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const [costPasteRefused, setCostPasteRefusedState] = useState(false);
  const [copyFailed, setCopyFailedState] = useState(false);
  const setCopyFailed = (failed: boolean): void => {
    setCopyFailedState(failed);
    onCopyResult?.(failed);
  };
  // Told to the card as well as held here, or a grid clears only its own and the card reads the other.
  const setCostPasteRefused = (refused: boolean): void => {
    setCostPasteRefusedState(refused);
    onCostPasteResult?.(refused);
  };
  const [menuIsCopy, setMenuIsCopy] = useState(false);
  /** Where the pointer last was in the grid, which is where a menu the keys open belongs. */
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const container = useRef<HTMLDivElement>(null);
  /** The cell the pointer went down on, held until it either enters another cell or is let go. */
  const pressed = useRef<CellAddress | null>(null);
  /** A shift-click extends from here, so there is something to extend from before any drag. */
  const anchored = useRef<CellAddress | null>(null);
  /** Set when a press turned into a selection, so the click it ends with does not also open a cell. */
  const claimed = useRef(false);
  /** Set while the fill handle is held, so entering a cell moves the fill rather than the block. */
  const filling = useRef(false);
  const [reach, setReach] = useState<Span | null>(null);
  /** Read by the window's pointerup, which is bound once and would otherwise see the first render. */
  const finishFill = useRef<() => void>(() => undefined);

  useEffect(() => {
    setRect(null);
    setReach(null);
    setMenuAt(null);
    setCostPasteRefused(false);
    setCopyFailed(false);
  }, [resetOn]);

  // A press that ends anywhere ends the drag, including outside the grid.
  useEffect(() => {
    const up = (): void => {
      pressed.current = null;
      if (filling.current) finishFill.current();
    };
    window.addEventListener('pointerup', up);
    return () => window.removeEventListener('pointerup', up);
  }, []);

  // A press outside drops the block; the menu counts as inside, or opening it would drop the block.
  useEffect(() => {
    const down = (event: PointerEvent): void => {
      const target = event.target as Node | null;
      if (!target || container.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-cell-menu]')) return;
      setRect(null);
      setReach(null);
      setMenuAt(null);
    };
    window.addEventListener('pointerdown', down);
    return () => window.removeEventListener('pointerdown', down);
  }, []);

  const bounds = useCallback((): { rowSpan: [number, number]; colSpan: [number, number] } | null => {
    if (!rect) return null;
    const rowSpan = spanOf(rows, rect.anchor.row, rect.focus.row);
    const colSpan = spanOf(cols, rect.anchor.col, rect.focus.col);
    return rowSpan && colSpan ? { rowSpan, colSpan } : null;
  }, [rect, rows, cols]);

  const within = (index: number, [first, last]: [number, number]): boolean => index >= first && index <= last;

  const inBlock = (row: string, col: string): boolean => {
    const box = bounds();
    if (!box) return false;
    return within(rows.indexOf(row), box.rowSpan) && within(cols.indexOf(col), box.colSpan);
  };

  const inReach = (row: string, col: string): boolean =>
    !!reach && within(rows.indexOf(row), reach.rows) && within(cols.indexOf(col), reach.cols);

  const selected = (row: string, col: string): boolean => inBlock(row, col) || inReach(row, col);

  /** Only a grid that can be written to offers it: a handle that fills nothing would be a lie. */
  const canFill = !!writeCells;

  const edges = (row: string, col: string): CellEdges => {
    const box = bounds();
    if (!box || !selected(row, col)) return { top: false, right: false, bottom: false, left: false };
    const rowIndex = rows.indexOf(row);
    const colIndex = cols.indexOf(col);
    if (!inBlock(row, col) && reach) {
      return {
        top: rowIndex === reach.rows[0],
        bottom: rowIndex === reach.rows[1],
        left: colIndex === reach.cols[0],
        right: colIndex === reach.cols[1],
        pending: true,
      };
    }
    const bottom = rowIndex === box.rowSpan[1];
    const right = colIndex === box.colSpan[1];
    return {
      top: rowIndex === box.rowSpan[0],
      bottom,
      left: colIndex === box.colSpan[0],
      right,
      grip: canFill && !reach && bottom && right,
    };
  };

  /** A row the block only reaches across is drawn as part of it but is not one of these. */
  const addresses = (): CellAddress[][] => {
    const box = bounds();
    if (!box) return [];
    return rows
      .slice(box.rowSpan[0], box.rowSpan[1] + 1)
      .filter(row => !passiveRows?.includes(row))
      .map(row => cols.slice(box.colSpan[0], box.colSpan[1] + 1).map(col => ({ row, col })));
  };

  /** The block as indices, which is what the fill works in. */
  const blockSpan = (): Span | null => {
    const box = bounds();
    return box ? { rows: box.rowSpan, cols: box.colSpan } : null;
  };

  /** The handle is drawn over the corner's own edge, so a press there is a press on it. */
  const onGrip = (row: string, col: string, event: React.PointerEvent): boolean => {
    if (!edges(row, col).grip) return false;
    const box = (event.currentTarget as Element).getBoundingClientRect();
    return event.clientX >= box.right - GRIP_REACH && event.clientY >= box.bottom - GRIP_REACH;
  };

  // The block takes the keys and the clipboard, so it takes the focus: the press could not.
  const takeFocus = (): void => container.current?.focus({ preventScroll: true });

  // Neither a click on the cell nor the start of a new block.
  const startFill = (event: React.PointerEvent): void => {
    event.preventDefault();
    filling.current = true;
    claimed.current = true;
    // Closes an open cell, keeping what was typed, so Esc and the drag back work as from a closed one.
    takeFocus();
  };

  // Only the handle: a press anywhere else in the field is somebody placing the caret.
  const openCellHandlers = (row: string, col: string) => ({
    onPointerDown: (event: React.PointerEvent): void => {
      if (event.button === 0 && onGrip(row, col, event)) startFill(event);
    },
  });

  const cellHandlers = (row: string, col: string) => ({
    onPointerDown: (event: React.PointerEvent): void => {
      // The secondary button must not reach the cell: a press focuses it, and focus opens it for typing.
      if (event.button !== 0) {
        event.preventDefault();
        return;
      }
      if (onGrip(row, col, event)) {
        startFill(event);
        return;
      }
      const anchor = rect?.anchor ?? anchored.current;
      if (event.shiftKey && anchor) {
        setRect({ anchor, focus: { row, col } });
        // The press must not reach the cell: landing on a cell is what opens it for typing.
        event.preventDefault();
        claimed.current = true;
        takeFocus();
        return;
      }
      claimed.current = false;
      pressed.current = { row, col };
      anchored.current = { row, col };
      lastPoint.current = { x: event.clientX, y: event.clientY };
      // A fresh press drops whatever was selected, as pressing a cell in a spreadsheet does.
      setRect(null);
    },
    onPointerEnter: (event: React.PointerEvent): void => {
      lastPoint.current = { x: event.clientX, y: event.clientY };
      if (filling.current) {
        if (event.buttons === 0) {
          filling.current = false;
          setReach(null);
          return;
        }
        const block = blockSpan();
        if (block) setReach(fillReach(block, rows.indexOf(row), cols.indexOf(col)));
        return;
      }
      const from = pressed.current;
      if (!from) return;
      // Released outside the window sends no pointerup, so the press is over whatever we last saw.
      if (event.buttons === 0) {
        pressed.current = null;
        return;
      }
      // One cell is a click, not a drag: the block starts at the second cell the pointer reaches.
      if (from.row === row && from.col === col) return;
      setRect({ anchor: from, focus: { row, col } });
      claimed.current = true;
      takeFocus();
    },
    onContextMenu: (event: React.MouseEvent): void => {
      event.preventDefault();
      // Asked for outside the block, the menu takes the cell it was asked on, as a spreadsheet does.
      if (!inBlock(row, col)) {
        anchored.current = { row, col };
        setRect({ anchor: { row, col }, focus: { row, col } });
      }
      setCostPasteRefused(false);
      setCopyFailed(false);
      setMenuIsCopy(false);
      setMenuAt({ x: event.clientX, y: event.clientY });
      takeFocus();
    },
  });

  const claimedClick = (): boolean => {
    const was = claimed.current;
    claimed.current = false;
    return was;
  };

  const editable = (row: string, col: string): boolean => (isEditable ? isEditable(row, col) : true);

  const write = (cells: { row: string; col: string; value: string }[]): void => {
    if (!writeCells) return;
    const allowed = cells.filter(cell => editable(cell.row, cell.col));
    if (allowed.length) writeCells(allowed);
  };

  // Hands the keys back to the grid: without it the focus dies with the item that was pressed.
  const shutMenu = (): void => {
    setMenuAt(null);
    takeFocus();
  };

  const hasBlock = (): boolean => bounds() !== null;
  /** Where a menu goes when the pointer has not been near the grid, as after a keyboard selection. */
  const middleOfGrid = (): { x: number; y: number } => {
    const box = container.current?.getBoundingClientRect();
    return box ? { x: box.left + box.width / 3, y: box.top + box.height / 3 } : { x: 80, y: 80 };
  };

  /** The block as text, in one figure or the other. */
  const asText = (unit: CopyUnit): string | null => {
    const block = addresses();
    if (!block.length) return null;
    return toTsv(block.map(row => row.map(at => readCell(at.row, at.col, unit))));
  };

  // Resolved here rather than at each caller: the copy key and the menu must read 'lead' the same way.
  const remember = (unit: CopyUnit, text: string): void => {
    lastCostCopy = unit === 'cost' || (unit === 'lead' && leadIsCost) ? text : null;
  };

  // Two figures cannot answer the copy key alone, so it opens the menu instead of guessing.
  const onCopy = (event: React.ClipboardEvent): void => {
    if (!hasBlock()) return;
    event.preventDefault();
    if (twoFigures) {
      setCostPasteRefused(false);
      setCopyFailed(false);
      setMenuIsCopy(true);
      setMenuAt(lastPoint.current ?? middleOfGrid());
      return;
    }
    const text = asText('lead');
    if (text === null) return;
    event.clipboardData.setData('text/plain', text);
    remember('lead', text);
    // The message names this as the way out, so it cannot still be on screen once it has worked.
    setCopyFailed(false);
  };

  const copyAs = (unit: CopyUnit): void => {
    const text = asText(unit);
    if (text === null) return;
    remember(unit, text);
    setCopyFailed(false);
    shutMenu();
    // The browser can refuse the clipboard, and an item that quietly did nothing is worse than one that says so.
    copyToClipboard(text).catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.error('Copying the block to the clipboard was refused', error);
      setCopyFailed(true);
    });
  };

  // A grid that cannot hold a blank keeps what is there rather than taking the blank.
  const pasteable = (cells: { row: string; col: string; value: string }[]): typeof cells =>
    allowClear ? cells : cells.filter(cell => cell.value !== '');

  const onPaste = (event: React.ClipboardEvent): void => {
    const text = event.clipboardData.getData('text');
    // Normalised the way fromTsv does first: a spreadsheet ends even one cell with a line break.
    const body = text.replace(/\r\n?/g, '\n').replace(/\n$/, '');
    const single = !body.includes('\t') && !body.includes('\n');
    // A field that holds money is where a copied rate belongs, so the refusal is not about that one.
    const intoMoney = [event.target as Element | null, document.activeElement].some(node =>
      node instanceof Element ? !!node.closest(moneyFieldSelector) : false,
    );
    // Refused ahead of everything else: an open field would otherwise swallow the paste natively.
    if (!leadIsCost && !intoMoney && lastCostCopy !== null && text === lastCostCopy) {
      event.preventDefault();
      setCostPasteRefused(true);
      return;
    }
    setCostPasteRefused(false);
    // One value typed into an open cell belongs to that field; over a block it fills the block.
    const intoField =
      (event.target as HTMLElement | null)?.tagName === 'INPUT' || document.activeElement?.tagName === 'INPUT';
    if (single && (intoField || !hasBlock())) return;
    const box = bounds();
    if (!box || !writeCells) return;

    if (single) {
      event.preventDefault();
      const [firstRow, lastRow] = box.rowSpan;
      const [firstCol, lastCol] = box.colSpan;
      const filled: { row: string; col: string; value: string }[] = [];
      for (let down = firstRow; down <= lastRow; down += 1) {
        for (let across = firstCol; across <= lastCol; across += 1) {
          const row = rows[down];
          const col = cols[across];
          if (row !== undefined && col !== undefined) filled.push({ row, col, value: text.trim() });
        }
      }
      write(pasteable(filled));
      return;
    }

    const block = fromTsv(text);
    if (!block.length) return;
    event.preventDefault();

    // Cut off at the grid's edges rather than wrapping: a paste running off the side has nowhere to go.
    const [firstRow] = box.rowSpan;
    const [firstCol] = box.colSpan;
    const cells: { row: string; col: string; value: string }[] = [];
    block.forEach((line, downBy) => {
      line.forEach((value, acrossBy) => {
        const row = rows[firstRow + downBy];
        const col = cols[firstCol + acrossBy];
        if (row !== undefined && col !== undefined) cells.push({ row, col, value: value.trim() });
      });
    });
    write(pasteable(cells));

    // What was pasted is what is selected afterwards, so it can be read back or cleared in one go.
    const lastRow = rows[Math.min(firstRow + block.length - 1, rows.length - 1)];
    const widest = Math.max(...block.map(line => line.length));
    const lastCol = cols[Math.min(firstCol + widest - 1, cols.length - 1)];
    setRect({ anchor: { row: rows[firstRow], col: cols[firstCol] }, focus: { row: lastRow, col: lastCol } });
  };

  const clearCells = (): void => {
    if (!allowClear) return;
    write(
      addresses()
        .flat()
        .map(at => ({ ...at, value: '' })),
    );
    shutMenu();
  };
  const hasEditable = (): boolean =>
    !!writeCells &&
    addresses()
      .flat()
      .some(at => editable(at.row, at.col));

  /** Writes the reach from the block, repeated, and selects the two together as a spreadsheet does. */
  finishFill.current = (): void => {
    filling.current = false;
    const block = blockSpan();
    setReach(null);
    if (!block || !reach) return;
    // Only rows and columns holding a typeable cell repeat: a name, a heading or a total is not a figure.
    const spanned = ([first, last]: [number, number]): number[] =>
      Array.from({ length: last - first + 1 }, (_, offset) => first + offset);
    const rowSources = spanned(block.rows).filter(
      index => !passiveRows?.includes(rows[index]) && cols.some(col => editable(rows[index], col)),
    );
    const colSources = spanned(block.cols).filter(index => rows.some(row => editable(row, cols[index])));
    const cells: { row: string; col: string; value: string }[] = [];
    spanned(reach.rows).forEach(down => {
      const fromRow = fillSource(rowSources, block.rows, down);
      spanned(reach.cols).forEach(across => {
        const fromCol = fillSource(colSources, block.cols, across);
        if (fromRow === null || fromCol === null) return;
        const from = { row: rows[fromRow], col: cols[fromCol] };
        const to = { row: rows[down], col: cols[across] };
        if (fillsFrom(from, to)) cells.push({ ...to, value: readForFill(from.row, from.col).trim() });
      });
    });
    write(pasteable(cells));
    const top = Math.min(block.rows[0], reach.rows[0]);
    const bottom = Math.max(block.rows[1], reach.rows[1]);
    const left = Math.min(block.cols[0], reach.cols[0]);
    const right = Math.max(block.cols[1], reach.cols[1]);
    setRect({ anchor: { row: rows[top], col: cols[left] }, focus: { row: rows[bottom], col: cols[right] } });
    takeFocus();
  };

  /** One step of the block's reaching edge, which is the end a shift-arrow moves. */
  const stretch = (downBy: number, acrossBy: number): void => {
    if (!rect) return;
    const rowIndex = rows.indexOf(rect.focus.row);
    const colIndex = cols.indexOf(rect.focus.col);
    if (rowIndex === -1 || colIndex === -1) return;
    const row = rows[Math.min(Math.max(rowIndex + downBy, 0), rows.length - 1)];
    const col = cols[Math.min(Math.max(colIndex + acrossBy, 0), cols.length - 1)];
    setRect({ anchor: rect.anchor, focus: { row, col } });
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    // The whole table, headings and totals included: what copying into a spreadsheet means.
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a' && rows.length && cols.length) {
      event.preventDefault();
      setRect({
        anchor: { row: rows[0], col: cols[0] },
        focus: { row: rows[rows.length - 1], col: cols[cols.length - 1] },
      });
      takeFocus();
      return;
    }
    if (!rect) return;
    const steps: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    if (event.shiftKey && steps[event.key]) {
      event.preventDefault();
      stretch(...steps[event.key]);
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      clearCells();
      return;
    }
    if (event.key === 'Escape') {
      // Let go of a fill first, as a spreadsheet does: the block it was dragged from stays.
      if (filling.current) {
        filling.current = false;
        setReach(null);
        return;
      }
      setRect(null);
      setMenuAt(null);
    }
  };

  const anchorAt = (row: string, col: string): void => {
    anchored.current = { row, col };
    setRect({ anchor: { row, col }, focus: { row, col } });
  };

  return {
    selected,
    edges,
    copyAs,
    clearCells,
    hasEditable,
    hasBlock,
    menuAt,
    menuIsCopy,
    closeMenu: shutMenu,
    costPasteRefused,
    copyFailed,
    cellHandlers,
    openCellHandlers,
    claimedClick,
    anchorAt,
    containerHandlers: { ref: container, tabIndex: -1, onCopy, onPaste, onKeyDown },
    // The banners describe the last press, so a cleared block leaves nothing for them to be about.
    clear: () => {
      setRect(null);
      setReach(null);
      setCostPasteRefused(false);
      setCopyFailed(false);
    },
  };
};

/** A number field empties any value it cannot parse, and a comma is how a decimal is typed here. */
export const numberFieldHolds = (value: string): boolean => value === '' || !Number.isNaN(Number(value));

/** Text selection is off, as in a spreadsheet, and put back for every field so none is forgotten. */
export const rangeContainer = 'select-none outline-none [&_input]:select-text [&_textarea]:select-text';

/** An overlay, not a fill: Tailwind would settle a fill against the grid's own tints by emit order. */
export const rangeOverlay = (edges: CellEdges, options?: { hatched?: boolean; open?: boolean }): string =>
  cx(
    'relative after:pointer-events-none after:absolute after:inset-0 after:content-[""]',
    'after:border-secondary',
    // Hatched where the block only reaches across: a spreadsheet's mark for a cell that does not apply.
    options?.hatched &&
      'after:bg-[repeating-linear-gradient(135deg,transparent_0_4px,color-mix(in_oklab,var(--secondary)_18%,transparent)_4px_8px)]',
    // The cell being typed into is outlined but not washed, as a spreadsheet's active cell is.
    !options?.hatched && !options?.open && (edges.pending ? 'after:bg-secondary/5' : 'after:bg-secondary/10'),
    edges.pending && 'after:border-dashed',
    // The fill handle, ringed apart from the border; under the sticky name column (z-10) once scrolled.
    edges.grip &&
      'before:absolute before:-right-[3px] before:-bottom-[3px] before:z-[1] before:size-2 before:cursor-crosshair before:bg-secondary before:shadow-[0_0_0_1px_var(--card)] before:content-[""]',
    edges.top && 'after:border-t-2',
    edges.bottom && 'after:border-b-2',
    edges.left && 'after:border-l-2',
    edges.right && 'after:border-r-2',
  );
